/** Hat durumu, önerilen senaryo, kalite ihlali, karar bekliyor, KPI karışımı. */

import { CURRENT_SCENARIO } from "./frequencyScenarios.js";
import {
  CLOSE_COST_PCT,
  QUALITY_OVER_CAPACITY,
  QUALITY_OVER_TARGET,
} from "./freqoptDecisionConfig.js";
import { effectiveParams, hashParams } from "./freqoptParams.js";

export const HAT_ANA = {
  YOK: "sonuc_yok",
  GUNCEL: "guncel",
  ESKI: "eski",
  HATALI: "hatali",
};

export const HAT_ALT = {
  KARAR: "karar_bekliyor",
  ONERI: "oneri_hazir",
  ONAYLI: "onayli",
  YAYINDA: "yayinda",
};

const CURRENT_ID = CURRENT_SCENARIO.id;

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function scenarioBlocks(ozet) {
  const bloklar = (ozet && ozet.senaryolar) || {};
  const sira = ozet?.senaryo_sirasi || Object.keys(bloklar);
  return (sira.length ? sira : Object.keys(bloklar))
    .filter((id) => id && id !== CURRENT_ID)
    .map((id) => ({ id, blok: bloklar[id] }))
    .filter((x) => x.blok && x.blok.hata == null);
}

export function occupancyThreshold(params) {
  const doluluk = Number(params?.doluluk);
  if (!Number.isFinite(doluluk)) return 0.85;
  return doluluk / 100;
}

export function maxOccupancy(blok) {
  if (!blok) return null;
  const nested = blok.doluluk_profili || {};
  return num(blok.max_doluluk ?? nested.max_doluluk);
}

export function qualityViolation(blok, params) {
  const max = maxOccupancy(blok);
  if (max == null) return false;
  if (QUALITY_OVER_CAPACITY && max > 1 + 1e-9) return true;
  if (!QUALITY_OVER_TARGET) return false;
  return max > occupancyThreshold(params) + 1e-9;
}

export function violationMagnitude(blok, params) {
  const max = maxOccupancy(blok);
  if (max == null) return 0;
  return Math.max(0, max - occupancyThreshold(params));
}

export function recommendedScenarioId(ozet) {
  let best = null;
  let bestFark = Infinity;
  for (const { id, blok } of scenarioBlocks(ozet)) {
    const fark = num(blok.fark_maliyet);
    if (fark == null) continue;
    if (fark < bestFark) {
      bestFark = fark;
      best = id;
    }
  }
  if (best == null || bestFark > 0) return CURRENT_ID;
  return best;
}

function samePlan(a, b) {
  if (!a || !b) return false;
  return (
    num(a.arac) === num(b.arac) &&
    num(a.sefer) === num(b.sefer) &&
    (num(a.max_doluluk) ?? null) === (num(b.max_doluluk) ?? null)
  );
}

export function closeCostPair(ozet, pct = CLOSE_COST_PCT) {
  const aday = scenarioBlocks(ozet)
    .map(({ id, blok }) => ({ id, blok, maliyet: num(blok.maliyet) }))
    .filter((x) => x.maliyet != null)
    .sort((a, b) => a.maliyet - b.maliyet);
  if (aday.length < 2) return null;
  const cheapest = aday[0];
  const next = aday.find((x) => !samePlan(cheapest.blok, x.blok));
  if (!next || !cheapest.maliyet) return null;
  const gapPct = ((next.maliyet - cheapest.maliyet) / cheapest.maliyet) * 100;
  if (gapPct >= pct) return null;
  return { cheaperId: cheapest.id, otherId: next.id, gapPct };
}

export function isCloseCost(ozet, pct = CLOSE_COST_PCT) {
  return closeCostPair(ozet, pct) != null;
}

export function selectedScenarioId(ozet, selectedByHat, hatNo) {
  const key = String(hatNo ?? ozet?.hat_no ?? "").trim();
  const picked = selectedByHat?.[key];
  if (picked === CURRENT_ID) return CURRENT_ID;
  if (picked && (ozet?.senaryolar || {})[picked]) return picked;
  return recommendedScenarioId(ozet);
}

export function isApproved(approvedHats, hatNo, selectedId) {
  const rec = (approvedHats || {})[String(hatNo).trim()];
  if (!rec) return false;
  if (typeof rec === "string") return rec === selectedId || rec === true;
  if (rec === true) return true;
  if (selectedId && rec.scenarioId && rec.scenarioId !== selectedId) return false;
  return Boolean(rec.scenarioId || rec.approved);
}

export function hatAnaDurum({
  ozet,
  runDurum,
  storedSignature,
  currentSignature,
}) {
  const failed = runDurum === "failed";
  if (failed) return HAT_ANA.HATALI;
  const hasResult = Boolean(ozet?.senaryolar && Object.keys(ozet.senaryolar).length);
  if (!hasResult) return HAT_ANA.YOK;
  if (storedSignature && currentSignature && storedSignature !== currentSignature) {
    return HAT_ANA.ESKI;
  }
  return HAT_ANA.GUNCEL;
}

export function hatAltDurum({
  ana,
  ozet,
  approved,
  publishedAt = null,
  closePct = CLOSE_COST_PCT,
}) {
  if (ana === HAT_ANA.YOK || ana === HAT_ANA.HATALI) return null;
  // Eskimiş sonuçta Yayında/Onaylı gösterme — Yeniden bakılmalı (ana=eski)
  if (ana === HAT_ANA.ESKI) return null;
  if (publishedAt && approved) return HAT_ALT.YAYINDA;
  if (approved) return HAT_ALT.ONAYLI;
  const recId = recommendedScenarioId(ozet);
  if (recId === CURRENT_ID) return HAT_ALT.ONERI;
  if (isCloseCost(ozet, closePct)) return HAT_ALT.KARAR;
  return HAT_ALT.ONERI;
}

export function hatKarar(input) {
  const hatNo = String(input.hatNo || input.ozet?.hat_no || "").trim();
  const params =
    input.params ||
    effectiveParams(input.globalParams, input.lineOverrides, hatNo);
  const currentSignature = hashParams(params);
  const ana = hatAnaDurum({
    ozet: input.ozet,
    runDurum: input.runDurum,
    storedSignature: input.storedSignature,
    currentSignature,
  });
  const selectedId = selectedScenarioId(input.ozet, input.selectedScenarioByHat, hatNo);
  const approved = isApproved(input.approvedHats, hatNo, selectedId);
  const publishedAt = input.publishedAt ?? input.published_at ?? null;
  const alt = hatAltDurum({
    ana,
    ozet: input.ozet,
    params,
    approved,
    publishedAt,
    closePct: input.closePct,
  });
  const recId = recommendedScenarioId(input.ozet);
  const recBlok = recId && recId !== CURRENT_ID ? input.ozet?.senaryolar?.[recId] : null;
  const selBlok = selectedId && selectedId !== CURRENT_ID ? input.ozet?.senaryolar?.[selectedId] : null;
  return {
    hatNo,
    ana,
    alt,
    params,
    currentSignature,
    recommendedId: recId,
    selectedId,
    approved,
    publishedAt: publishedAt || null,
    publishedSenaryoId: input.publishedSenaryoId ?? input.published_senaryo_id ?? null,
    maliyetArtiyor:
      recId === CURRENT_ID &&
      scenarioBlocks(input.ozet).some((x) => {
        const f = num(x.blok.fark_maliyet);
        return f != null && f > 0;
      }),
    qualityOnRecommended: recId === CURRENT_ID ? false : qualityViolation(recBlok, params),
    qualityOnSelected: selectedId === CURRENT_ID ? false : qualityViolation(selBlok, params),
    violationMagnitude: recId === CURRENT_ID ? 0 : violationMagnitude(recBlok, params),
    savingsAtRisk: recId === CURRENT_ID ? 0 : Math.abs(num(recBlok?.fark_maliyet) || 0),
    closePair: closeCostPair(input.ozet, input.closePct),
  };
}

export function compareKararBekleyen(a, b) {
  const mag = (b.violationMagnitude || 0) - (a.violationMagnitude || 0);
  if (Math.abs(mag) > 1e-9) return mag;
  const sav = (b.savingsAtRisk || 0) - (a.savingsAtRisk || 0);
  if (Math.abs(sav) > 1e-9) return sav;
  return String(a.hatNo).localeCompare(String(b.hatNo), "tr");
}

function emptyMix() {
  return {
    hatSayisi: 0,
    guncelHat: 0,
    eskiHat: 0,
    hataliHat: 0,
    sonucYok: 0,
    onayli: 0,
    ozelParametre: 0,
    ihlalliSecim: 0,
    maliyet: 0,
    mevcutMaliyet: 0,
    farkMaliyet: 0,
    farkYillik: 0,
    arac: 0,
    mevcutArac: 0,
    farkArac: 0,
    sefer: 0,
    mevcutSefer: 0,
    farkSefer: 0,
  };
}

/**
 * KPI karışımı: yalnız güncel + seçili senaryo.
 * Eski, hatalı ve sonuçsuz hatlar girmez.
 */
export function mixedKpis({
  hatNos = null,
  hatOzetleri,
  hatDurumlari,
  globalParams,
  lineOverrides,
  selectedScenarioByHat,
  approvedHats,
  paramSignatureByHat,
}) {
  const mix = emptyMix();
  const list = hatNos || Object.keys(hatOzetleri || {});
  for (const hatNo of list) {
    const ozet = (hatOzetleri || {})[hatNo];
    const karar = hatKarar({
      hatNo,
      ozet,
      runDurum: (hatDurumlari || {})[hatNo]?.durum,
      globalParams,
      lineOverrides,
      selectedScenarioByHat,
      approvedHats,
      storedSignature: (paramSignatureByHat || {})[hatNo],
    });
    if (karar.ana === HAT_ANA.ESKI) {
      mix.eskiHat += 1;
      continue;
    }
    if (karar.ana === HAT_ANA.HATALI) {
      mix.hataliHat += 1;
      continue;
    }
    if (karar.ana === HAT_ANA.YOK) {
      mix.sonucYok += 1;
      continue;
    }
    mix.guncelHat += 1;
    const sel = ozet?.senaryolar?.[karar.selectedId];
    const mevcut = ozet?.senaryolar?.[CURRENT_ID];
    if (karar.selectedId === CURRENT_ID) {
      mix.hatSayisi += 1;
      mix.maliyet += num(mevcut?.maliyet) || 0;
      mix.mevcutMaliyet += num(mevcut?.maliyet) || 0;
      mix.arac += num(mevcut?.arac) || 0;
      mix.mevcutArac += num(mevcut?.arac) || 0;
      mix.sefer += num(mevcut?.sefer) || 0;
      mix.mevcutSefer += num(mevcut?.sefer) || 0;
      if (karar.approved) mix.onayli += 1;
      if (Object.keys((lineOverrides || {})[hatNo] || {}).length) mix.ozelParametre += 1;
      continue;
    }
    if (!sel || sel.hata != null) continue;
    mix.hatSayisi += 1;
    mix.maliyet += num(sel.maliyet) || 0;
    mix.mevcutMaliyet += num(mevcut?.maliyet) || 0;
    mix.farkMaliyet += num(sel.fark_maliyet) || 0;
    mix.farkYillik += num(sel.fark_yillik) || 0;
    mix.arac += num(sel.arac) || 0;
    mix.mevcutArac += num(mevcut?.arac) || 0;
    mix.farkArac += num(sel.fark_arac) || 0;
    mix.sefer += num(sel.sefer) || 0;
    mix.mevcutSefer += num(mevcut?.sefer) || 0;
    mix.farkSefer += num(sel.fark_sefer) || 0;
    if (karar.approved) mix.onayli += 1;
    if (karar.qualityOnSelected) mix.ihlalliSecim += 1;
    if (Object.keys((lineOverrides || {})[hatNo] || {}).length) mix.ozelParametre += 1;
  }
  return mix;
}
